import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { AppAvatar } from 'components/core/avatar'
import { C1Card } from 'components/core/card/C1Card'
import { TaskPrimaryActionsV2 } from 'components/core/task-list-v2/task-list-v2-components/TaskPrimaryActionsV2'
import { SubtleLink, TaskTitleFormattedMessage } from 'components/pages/task/details/content/TaskTitleFormattedMessage'
import { TaskDrawer } from 'components/pages/task/details/drawer/TaskDrawer'
import { useDrawer } from 'hooks/useDrawer'
import { TaskValue } from 'lib/actions/task'
import { UserContext } from 'lib/context/app'
import { routes } from 'lib/routes'
import { FC, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

export const EndUserTaskListItem: FC<{ row: TaskValue }> = ({ row }) => {
    const [showDrawer, closeDrawer] = useDrawer()
    const intl = useIntl()

    const { user } = useContext(UserContext)

    const isForSelf = user?.id === row.userId

    const openDrawer = (): void => {
        showDrawer(<TaskDrawer taskId={row.id} close={closeDrawer} />, { variant: 'md' })
    }

    const handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
        const targetElement = e.target as HTMLElement

        if (!targetElement.closest('button, a')) {
            const isLeftClick = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
            if (isLeftClick) {
                openDrawer()
            } else {
                window.open(`/task/${row.id}`, '_blank')
            }
        }
    }

    return <C1Card
        data-dd-action-name="View task"
        hasInteractiveChildren
        onClick={handleClick}
        sx={{
            borderColor: 'border.subtle',
            my: 0,
            px: 4,
            py: 1,
            minHeight: '60px',
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            alignItems: 'center',
            borderRadius: 0,
            borderBottom: 'none',
            cursor: 'pointer',
            position: 'relative',
            '&:last-of-type': {
                borderBottom: '1px solid',
                borderColor: 'border.subtle',
            },
            '&:hover': {
                zIndex: 1,
                borderColor: 'transparent',
                boxShadow: (theme): string => `inset 0 0 0 2px ${theme.palette.primary.main}`,
            },
            '&:has(:focus-visible)': {
                zIndex: 1,
                borderColor: 'transparent',
                boxShadow: (theme): string => `inset 0 0 0 2px ${theme.palette.primary.main}`,
            },
        }}
    >
        {/* Stretched link for keyboard accessibility - covers entire card */}
        <Box
            component="button"
            type="button"
            onClick={openDrawer}
            aria-label={intl.formatMessage({ id: 'end_user_task_list_view_details', defaultMessage: 'View task details' })}
            sx={{
                position: 'absolute',
                inset: 0,
                opacity: 0,
                cursor: 'pointer',
                zIndex: 0,
            }}
        />
        <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between" width="100%" sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction="row" gap={2} alignItems="center">
                {row.app &&
                    <AppAvatar appId={row.app?.id} />
                }

                <Stack direction="row" alignItems="center">
                    <Typography variant="body1" component="div">
                        {
                            isForSelf && row.type?.taskType.oneofKind !== 'action' // We want to use the standard task title for actions
                                ? <Stack direction="row" gap={1}>
                                    <SubtleLink href={routes.applications.entitlement(row.appEntitlements?.[0]?.appId || '', row.appEntitlements?.[0]?.id || '')}>{row.appEntitlements?.[0]?.displayName}</SubtleLink>
                                    <FormattedMessage
                                        id="component_end_user_task_list_item_for_self"
                                        defaultMessage="in"
                                    />
                                    <SubtleLink href={routes.applications.detail(row.app?.id || '')}>{row.app?.displayName}</SubtleLink>
                                </Stack>
                                : <TaskTitleFormattedMessage task={row} />
                        }
                    </Typography>
                </Stack>
            </Stack>

            <TaskPrimaryActionsV2 task={row} variant="icon-button" />
        </Stack>
    </C1Card>
}

export const EndUserTaskListItemSkeleton: FC = () => {
    const width = Math.floor(Math.random() * (500 - 300)) + 300

    return <C1Card>
        <Stack direction="row" gap={2} alignItems="center">
            <Skeleton variant="circular" height={36} width={36} />
            <Stack gap={1}>
                <Skeleton variant="text" height={24} width={width} />
            </Stack>
        </Stack>
    </C1Card>
}
